import type {
  EffectivePolicy,
  Identifiable,
  RotaTemplateScope,
  ScalingMetric,
  SchedulingRuleset,
  ShiftPattern,
  ShiftType as ContractsShiftType,
} from '@repo/contracts';

// Contracts own every template shape they define; the wizard imports them
// directly rather than maintaining a parallel model. The few wizard-only
// concerns are expressed as explicit, clearly-named extensions below.
export type { Site } from '@repo/contracts';
export type {
  Identifiable,
  RotaTemplateScope,
  ShiftSchedule,
  StaffAssignmentConfig,
  StaffFilters,
  SchedulingRule,
  SchedulingRuleset,
} from '@repo/contracts';

// Template Types
export type TemplateType = 'standard'; // Simplified to single type

/**
 * Named weekly-schedule presets for the day-picker UI. Not a persisted field —
 * shifts persist a contract `ShiftSchedule` (`'weekdays' | 'weekends' |
 * 'everyday' | number[]`). `'custom'` is the UI label for the `number[]` case.
 */
export type DayPattern = 'weekdays' | 'weekends' | 'everyday' | 'custom';

/** Maximum length of a template description (enforced in the wizard's Basics step). */
export const TEMPLATE_DESCRIPTION_MAX_LENGTH = 500;

export interface TemplateBasics {
  name: string;
  type: TemplateType;
  /** Contract `RotaTemplate.description` is required; the wizard defaults it to ''. */
  description: string;
  /** Contract scope: `{ sites?: Identifiable[] }`. Empty/undefined ⇒ all sites. */
  scope: RotaTemplateScope;
  /** Template-level demand scaling settings (drives the per-shift scaling + baseline). */
  demandScaling?: DemandScaling;
}

/**
 * Template-level demand-scaling settings — the UI editing model behind the
 * "Scale to demand" baseline editor. One `metric` drives the whole template; the
 * baseline is either a `weekly` total or seven `daily` figures (Mon–Sun). The
 * contract `RotaTemplate.baseline` (`Capacity` per day) is derived from this for
 * rotagen; each scaling shift's `staffing.scaling.model.metric` is set to `metric`.
 */
export interface DemandScaling {
  /** Whether this template scales headcount to demand at all. */
  enabled: boolean;
  /** The single demand metric the whole template scales against. */
  metric: ScalingMetric;
  /** Whether the baseline is a weekly total or seven per-day figures. */
  period: 'weekly' | 'daily';
  /** Baseline demand for a typical week (used when `period` is `weekly`). */
  weekly: number;
  /** Baseline demand per day, Mon–Sun (used when `period` is `daily`). */
  daily: number[];
}

/**
 * Wizard shift = the contract `ShiftPattern` (id, schedule, shiftType,
 * startTime, endTime, staffing) plus two genuinely UI-only fields. Staff
 * groups / tags / specific employees live inside `staffing.filters`; the
 * weekly schedule is a `ShiftSchedule` (ISO 1=Mon..7=Sun).
 */
export interface ShiftSet extends ShiftPattern {
  /** Display label shown in the wizard. UI-only; not part of `ShiftPattern`. */
  name: string;
  /** Optional work area. UI-only; not modelled by the contract. */
  workArea?: WorkArea;
}

/**
 * A previously-used shift configuration recorded for the wizard's
 * shift-suggestions feature. `time` is the canonical `"HH:mm-HH:mm"`
 * range; `count` is how many times this pattern was used historically.
 */
export interface ShiftSuggestion {
  time: string;
  shiftType: string;
  staffGroups: string[];
  tags: string[];
  employees: string[];
  count: number;
}

/**
 * Ergonomic, flat view of the scheduling rules for the Advanced Settings UI.
 * The wizard stores the canonical contract `SchedulingRuleset`; this flat shape
 * is converted to/from it by `scheduling-rules.adapter.ts`. Each field maps to
 * one contract `SchedulingRule` kind.
 */
export interface AdvancedSettings {
  overrideRestBreak?: number; // → RestBreakBetweenShiftsRule.hours
  allowSplitShifts: boolean; // → SplitShiftsRule.enabled
  maxSplitShiftsPerDay?: number; // → SplitShiftsRule.maxShiftsPerDay
  minDaysOffPerWeek?: number; // → MinimumDaysOffRule.daysOffPerWeek
  consecutiveDaysOff?: boolean; // → MinimumDaysOffRule.requireConsecutiveDaysOff
  maxConsecutiveDaysWorked?: number; // → MaxConsecutiveDaysRule.maxDays
}

export type WorkArea = Identifiable;

export interface WizardState {
  currentStep: 1 | 2 | 3 | 4;
  templateBasics: TemplateBasics;
  shifts: ShiftSet[];
  /** Canonical, contract-shaped scheduling rules. Edited via the flat adapter. */
  schedulingRules: SchedulingRuleset;
  lastSaved: number | null;
}

/**
 * Group type for step2-groups and step3-shifts components
 */
export interface Group {
  id: string;
  name: string;
  shiftType?: string;
  requiredTags?: string[];
}

/**
 * Employee record with denormalised JSONB staff groups and tags.
 * staffGroups and tags are stored as object arrays to match the database shape
 * and avoid additional mapping layers. The picker uses the embedded groups/tags
 * for the eligibility cascade; only `Identifiable` is persisted into filters.
 */
export interface Employee extends Identifiable {
  staffGroups: StaffGroup[];
  tags: Tag[];
}

export type Tag = Identifiable;

/**
 * The UI shift type, identical to the contracts `ShiftType`. The rotagen
 * boundary keys shift types by `id`.
 */
export type ShiftType = ContractsShiftType;

export type StaffGroup = Identifiable;

/** Response body of `GET /v1/sites/:siteId/rules`. */
export interface SiteRulesResponse {
  defaults: {
    policy: EffectivePolicy;
    advancedSettings: SchedulingRuleset;
  };
}
